import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, CalendarClock, MapPin, CheckCircle2, XCircle, ArrowLeft, Video } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

export default function ManageBooking() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [mode, setMode] = useState(""); // "" | "reschedule"
  const [form, setForm] = useState({ date: "", time: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(""); // "cancelled" | "requested" | "rescheduled"
  const [outcome, setOutcome] = useState(null);
  const [rDate, setRDate] = useState("");
  const [rSlot, setRSlot] = useState("");
  const [showRequest, setShowRequest] = useState(false);
  const [chosenProvider, setChosenProvider] = useState(null); // {enhancer_id, name, approval_id}
  const [reviewDate, setReviewDate] = useState("");
  const [reviewSlot, setReviewSlot] = useState("");
  const todayIso = new Date().toISOString().slice(0, 10);

  const { data: b, isLoading, error } = useQuery({
    queryKey: ["manage-booking", id, token],
    queryFn: () => api.get(`/booking-manage/${id}?token=${encodeURIComponent(token)}`).then((r) => r.data),
    retry: false,
  });
  const { data: preview } = useQuery({
    queryKey: ["cancel-preview", id, token],
    queryFn: () => api.get(`/booking-manage/${id}/cancel-preview?token=${encodeURIComponent(token)}`).then((r) => r.data),
    enabled: !!b && b.status !== "cancelled",
  });
  const { data: slotData, isFetching: slotsLoading } = useQuery({
    queryKey: ["manage-slots", id, token, rDate],
    queryFn: () => api.get(`/booking-manage/${id}/open-slots`, { params: { token, date: rDate } }).then((r) => r.data),
    enabled: !!rDate && mode === "reschedule" && !showRequest,
  });
  const slots = slotData?.slots || [];

  const needsReview = b?.review_status === "needs_center_review" && b?.status !== "cancelled";
  const { data: reviewOpts } = useQuery({
    queryKey: ["review-options", id, token],
    queryFn: () => api.get(`/booking-manage/${id}/review-options`, { params: { token } }).then((r) => r.data),
    enabled: !!needsReview && mode === "review",
  });
  const { data: reviewSlotData, isFetching: reviewSlotsLoading } = useQuery({
    queryKey: ["review-slots", id, token, chosenProvider?.enhancer_id, reviewDate],
    queryFn: () => api.get(`/booking-manage/${id}/open-slots`, { params: { token, date: reviewDate, enhancer_id: chosenProvider?.enhancer_id || "", approval_id: chosenProvider?.approval_id || "" } }).then((r) => r.data),
    enabled: !!(mode === "review" && chosenProvider && reviewDate),
  });
  const reviewSlots = reviewSlotData?.slots || [];

  const confirmProvider = async () => {
    if (!chosenProvider) { toast.error("Choose a provider first."); return; }
    setBusy(true);
    try {
      await api.post(`/booking-manage/${id}/choose-provider?token=${encodeURIComponent(token)}`, {
        enhancer_id: chosenProvider.enhancer_id,
        date: reviewDate || "", start_time: reviewSlot || "",
      });
      setResult("provider_chosen");
      toast.success(`Confirmed with ${chosenProvider.name}.`);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReschedule = async () => {
    if (!rDate || !rSlot) { toast.error("Pick a date and an open time."); return; }
    setBusy(true);
    try {
      await api.post(`/booking-manage/${id}/reschedule-direct?token=${encodeURIComponent(token)}`, { date: rDate, start_time: rSlot });
      setResult("rescheduled");
      toast.success("Rescheduled — same provider, same location.");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    const lateWarn = b?.is_late_now
      ? `\n\nThis is a LATE cancellation (inside the ${b.cancel_window_hours}h window).${b.deposit_paid > 0 ? ` Your ${money(b.deposit_paid)} deposit will be forfeited.` : ""}${b.cancel_fee > b.deposit_paid ? ` A ${money((b.cancel_fee - b.deposit_paid))} fee may be owed.` : ""}`
      : (b?.deposit_paid > 0 ? "\n\nYour deposit is eligible for a refund." : "");
    if (!window.confirm(`Cancel this appointment? This can't be undone.${lateWarn}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/booking-manage/${id}/cancel?token=${encodeURIComponent(token)}`);
      setOutcome(data.cancellation || null);
      setResult("cancelled");
      toast.success("Appointment cancelled");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const requestReschedule = async () => {
    if (!form.date || !form.time) { toast.error("Pick a preferred date and time."); return; }
    setBusy(true);
    try { await api.post(`/booking-manage/${id}/request-reschedule?token=${encodeURIComponent(token)}`, form); setResult("requested"); toast.success("Reschedule request sent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EB] flex items-start justify-center px-4 py-10" data-testid="manage-booking-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#4A5D6B] hover:text-[#144F75] mb-4"><ArrowLeft size={16} /> Enhancing Your World</Link>
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-[#144F75] text-white px-6 py-5">
            <div className="text-xs uppercase tracking-wider opacity-80">Manage your booking</div>
            <div className="text-xl font-serif mt-1">Reschedule or cancel</div>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#144F75]" /></div>
            ) : error ? (
              <div data-testid="manage-error" className="text-center py-6">
                <XCircle className="mx-auto text-[#B5794E]" size={32} />
                <p className="mt-3 text-sm text-[#4A5D6B]">{formatApiError(error.response?.data?.detail) || "This link is invalid or has expired."}</p>
                <Link to="/dashboard" className="eyw-btn !py-2 text-sm mt-4 inline-flex">Sign in to manage bookings</Link>
              </div>
            ) : result === "cancelled" ? (
              <div className="text-center py-6" data-testid="manage-cancelled">
                <CheckCircle2 className="mx-auto text-[#5C8A72]" size={36} />
                <p className="mt-3 text-[#1A242B] font-medium">Your appointment has been cancelled.</p>
                {outcome ? (
                  outcome.refund_due > 0 ? (
                    <p className="text-sm text-[#5C8A72] mt-2" data-testid="cancel-outcome">Your {money(outcome.refund_due)} deposit is eligible for a refund — we'll process it shortly.</p>
                  ) : outcome.late && outcome.forfeited > 0 ? (
                    <p className="text-sm text-[#B5794E] mt-2" data-testid="cancel-outcome">As this was a late cancellation, your {money(outcome.forfeited)} deposit was forfeited{outcome.extra_charge > 0 ? `, and a ${money(outcome.extra_charge)} fee is owed` : ""}.</p>
                  ) : outcome.extra_charge > 0 ? (
                    <p className="text-sm text-[#B5794E] mt-2" data-testid="cancel-outcome">A {money(outcome.extra_charge)} late-cancellation fee is owed on this appointment.</p>
                  ) : (
                    <p className="text-sm text-[#8FA89B] mt-1" data-testid="cancel-outcome">If you paid with a package, your session has been returned.</p>
                  )
                ) : (
                  <p className="text-sm text-[#8FA89B] mt-1">If you paid with a package, your session has been returned.</p>
                )}
              </div>
            ) : result === "requested" ? (
              <div className="text-center py-6" data-testid="manage-requested">
                <CheckCircle2 className="mx-auto text-[#5C8A72]" size={36} />
                <p className="mt-3 text-[#1A242B] font-medium">Reschedule request sent!</p>
                <p className="text-sm text-[#8FA89B] mt-1">Your provider will confirm a new time with you shortly.</p>
              </div>
            ) : result === "rescheduled" ? (
              <div className="text-center py-6" data-testid="manage-rescheduled">
                <CheckCircle2 className="mx-auto text-[#5C8A72]" size={36} />
                <p className="mt-3 text-[#1A242B] font-medium">Your appointment was moved!</p>
                <p className="text-sm text-[#5C8A72] mt-1">You're now booked for <b>{rDate} · {rSlot}</b> — same provider, same location.</p>
              </div>
            ) : result === "provider_chosen" ? (
              <div className="text-center py-6" data-testid="manage-provider-chosen">
                <CheckCircle2 className="mx-auto text-[#5C8A72]" size={36} />
                <p className="mt-3 text-[#1A242B] font-medium">You're all set!</p>
                <p className="text-sm text-[#5C8A72] mt-1">Confirmed with <b>{chosenProvider?.name}</b>{reviewDate && reviewSlot ? <> for <b>{reviewDate} · {reviewSlot}</b></> : null} at the same location. The center and your provider have been notified.</p>
              </div>
            ) : (
              <>
                <div className="bg-[#F9F7F3] rounded-2xl p-4">
                  <div className="text-lg font-semibold text-[#1A242B]">{b.service_title}</div>
                  <div className="mt-2 text-[#144F75] font-medium flex items-center gap-1.5"><CalendarClock size={16} /> {fmtDate(b.date)} · {fmtTimeRange(b.start_time)}</div>
                  {b.location && <div className="mt-1 text-sm text-[#4A5D6B] flex items-center gap-1.5"><MapPin size={14} /> {b.location}</div>}
                  {b.zoom_join_url && b.status !== "cancelled" && (
                    <a href={b.zoom_join_url} target="_blank" rel="noreferrer" data-testid="manage-zoom-join"
                      className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#5C8A72] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4d7561] transition-colors">
                      <Video size={15} /> Join Zoom session
                    </a>
                  )}
                  {b.status === "cancelled" && <div className="mt-2 text-xs text-red-500 font-medium">This booking is already cancelled.</div>}
                  {b.status !== "cancelled" && b.cancel_window_hours > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E0D8] text-xs text-[#4A5D6B]" data-testid="manage-policy-terms">
                      {b.is_late_now
                        ? <span className="text-[#B5794E]">You're inside the {b.cancel_window_hours}h cancellation window — {b.deposit_paid > 0 ? `your ${money(b.deposit_paid)} deposit would be forfeited` : "a late-cancellation fee may apply"}{b.cancel_fee > b.deposit_paid ? ` (fee ${money(b.cancel_fee)})` : ""}.</span>
                        : <span>Free cancellation up to {b.cancel_window_hours}h before your appointment{b.deposit_paid > 0 ? "; your deposit is refundable until then" : ""}.</span>}
                    </div>
                  )}
                </div>

                {needsReview && mode !== "review" && (
                  <div className="mt-5 rounded-2xl border border-[#D98E73]/40 bg-[#FBF3EC] p-4" data-testid="manage-review-banner">
                    <p className="text-sm font-semibold text-[#B5794E]">This appointment needs your attention</p>
                    <p className="text-xs text-[#4A5D6B] mt-1">{b.review_reason || "There's been a change with your provider."} Nothing has changed yet — your time is held. You can keep the same provider if they're available again, or choose another qualified provider at the same location.</p>
                    <button onClick={() => setMode("review")} data-testid="manage-review-btn" className="eyw-btn !py-2 text-sm mt-3">Review your options</button>
                  </div>
                )}

                {mode === "review" ? (
                  <div className="mt-5 space-y-3" data-testid="review-panel">
                    <p className="text-sm text-[#4A5D6B]">Choose a provider for your <b>{b.service_title}</b> at {b.location || "the same location"}.</p>
                    <div className="space-y-2">
                      {reviewOpts?.current_available_again && (
                        <button onClick={() => { setChosenProvider({ enhancer_id: reviewOpts.current_enhancer_id, name: reviewOpts.current_enhancer_name, approval_id: "" }); setReviewDate(""); setReviewSlot(""); }}
                          data-testid="review-keep-current"
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${chosenProvider?.enhancer_id === reviewOpts.current_enhancer_id ? "border-[#144F75] bg-[#144F75]/[0.05]" : "border-[#E5E0D8] hover:border-[#144F75]"}`}>
                          <div className="text-sm font-medium text-[#1A242B]">Keep {reviewOpts.current_enhancer_name} <span className="text-xs text-[#5C8A72]">· available again</span></div>
                        </button>
                      )}
                      {(reviewOpts?.options || []).map((o) => (
                        <button key={o.enhancer_id} onClick={() => { setChosenProvider(o); setReviewDate(""); setReviewSlot(""); }}
                          data-testid={`review-option-${o.enhancer_id}`}
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${chosenProvider?.enhancer_id === o.enhancer_id ? "border-[#144F75] bg-[#144F75]/[0.05]" : "border-[#E5E0D8] hover:border-[#144F75]"}`}>
                          <div className="text-sm font-medium text-[#1A242B]">{o.name}</div>
                          <div className="text-xs text-[#8FA89B]">Qualified for this service here</div>
                        </button>
                      ))}
                      {reviewOpts && !reviewOpts.current_available_again && (reviewOpts.options || []).length === 0 && (
                        <p className="text-sm text-[#B5794E]" data-testid="review-no-options">No qualified providers are available here right now. Please call us and we'll help — 859-979-5505.</p>
                      )}
                    </div>
                    {chosenProvider && (
                      <div className="pt-1 space-y-2">
                        <p className="text-xs text-[#4A5D6B]">Pick a new time with {chosenProvider.name} (optional — leave blank to keep {fmtDate(b.date)} · {fmtTimeRange(b.start_time)}).</p>
                        <input type="date" min={todayIso} value={reviewDate} onChange={(e) => { setReviewDate(e.target.value); setReviewSlot(""); }} data-testid="review-date" className="eyw-input" />
                        {reviewDate && (
                          reviewSlotsLoading ? <div className="text-sm text-[#8FA89B] flex items-center gap-1.5"><Loader2 className="animate-spin" size={14} /> Finding open times…</div>
                            : reviewSlots.length === 0 ? <p className="text-sm text-[#B5794E]" data-testid="review-no-slots">No open times that day — try another day.</p>
                              : <div className="flex flex-wrap gap-2" data-testid="review-slots">
                                {reviewSlots.map((s) => (
                                  <button key={s} type="button" onClick={() => setReviewSlot(s)} data-testid={`review-slot-${s}`}
                                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${reviewSlot === s ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{s}</button>
                                ))}
                              </div>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button onClick={confirmProvider} disabled={busy || !chosenProvider || (!!reviewDate && !reviewSlot)} data-testid="review-confirm" className="eyw-btn !py-2.5 text-sm flex-1 disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={16} /> : "Confirm"}</button>
                      <button onClick={() => { setMode(""); setChosenProvider(null); }} className="eyw-btn-ghost !py-2.5 text-sm">Back</button>
                    </div>
                  </div>
                ) : b.status !== "cancelled" && !needsReview && (mode === "reschedule" ? (
                  <div className="mt-5 space-y-3" data-testid="reschedule-form">
                    {!showRequest ? (
                      <>
                        <p className="text-sm text-[#4A5D6B]">Pick a new day, then choose an open time with {b.enhancer_name || "your provider"}.</p>
                        <input type="date" min={todayIso} value={rDate} onChange={(e) => { setRDate(e.target.value); setRSlot(""); }} data-testid="reschedule-date" className="eyw-input" />
                        {rDate && (
                          slotsLoading ? (
                            <div className="text-sm text-[#8FA89B] flex items-center gap-1.5"><Loader2 className="animate-spin" size={14} /> Finding open times…</div>
                          ) : slots.length === 0 ? (
                            <p className="text-sm text-[#B5794E]" data-testid="reschedule-no-slots">No open times with your provider that day. Try another day, or request a time below.</p>
                          ) : (
                            <div className="flex flex-wrap gap-2" data-testid="reschedule-slots">
                              {slots.map((s) => (
                                <button key={s} type="button" onClick={() => setRSlot(s)} data-testid={`reschedule-slot-${s}`}
                                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${rSlot === s ? "bg-[#144F75] text-white border-[#144F75]" : "bg-white border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{s}</button>
                              ))}
                            </div>
                          )
                        )}
                        <div className="flex gap-2 pt-1">
                          <button onClick={doReschedule} disabled={busy || !rSlot} data-testid="reschedule-confirm" className="eyw-btn !py-2.5 text-sm flex-1 disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={16} /> : "Confirm new time"}</button>
                          <button onClick={() => setMode("")} className="eyw-btn-ghost !py-2.5 text-sm">Back</button>
                        </div>
                        <button onClick={() => setShowRequest(true)} data-testid="reschedule-request-toggle" className="text-xs text-[#144F75] underline underline-offset-2">None of these work? Request a different time instead</button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-[#4A5D6B]">Tell us a time you'd prefer — your provider will confirm it with you.</p>
                        <div className="grid grid-cols-2 gap-3">
                          <input type="date" min={todayIso} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="reschedule-req-date" className="eyw-input" />
                          <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="reschedule-req-time" className="eyw-input" />
                        </div>
                        <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Anything we should know? (optional)" data-testid="reschedule-note" className="eyw-input w-full resize-none" />
                        <div className="flex gap-2">
                          <button onClick={requestReschedule} disabled={busy} data-testid="reschedule-submit" className="eyw-btn !py-2.5 text-sm flex-1 disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={16} /> : "Send request"}</button>
                          <button onClick={() => setShowRequest(false)} className="eyw-btn-ghost !py-2.5 text-sm">Back to open times</button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-5 space-y-2.5">
                    {preview && (
                      <div data-testid="cancel-fee-preview" className="rounded-xl border border-[#E5E0D8] bg-[#F9F7F3] p-3.5 text-sm">
                        <p className="font-medium text-[#1A242B] mb-1.5">If you cancel now</p>
                        {preview.late ? (
                          (preview.forfeited > 0 || preview.extra_charge > 0) ? (
                            <ul className="space-y-1">
                              {preview.forfeited > 0 && <li className="flex justify-between text-[#B5794E]"><span>Deposit forfeited</span><span>{money(preview.forfeited)}</span></li>}
                              {preview.extra_charge > 0 && <li className="flex justify-between text-[#B5794E]"><span>Additional late fee owed</span><span>{money(preview.extra_charge)}</span></li>}
                              <li className="flex justify-between font-semibold text-[#1A242B] pt-1.5 border-t border-[#E5E0D8]"><span>Total cost to cancel</span><span>{money((preview.forfeited + preview.extra_charge))}</span></li>
                            </ul>
                          ) : <p className="text-[#4A5D6B]">Late cancellation — but no deposit or fee applies to this booking.</p>
                        ) : (
                          <p className="text-[#5C8A72]">{preview.refund_due > 0 ? `No cancellation fee — your ${money(preview.refund_due)} deposit is eligible for a refund.` : "No cancellation fee, and no payment was taken."}</p>
                        )}
                        {preview.window_hours > 0 && !preview.late && <p className="text-xs text-[#8FA89B] mt-1.5">A fee applies only within {preview.window_hours}h of your appointment.</p>}
                      </div>
                    )}
                    <button onClick={() => setMode("reschedule")} data-testid="manage-reschedule-btn" className="eyw-btn !py-3 text-sm w-full justify-center">Reschedule appointment</button>
                    <button onClick={cancel} disabled={busy} data-testid="manage-cancel-btn" className="eyw-btn-ghost !py-3 text-sm w-full justify-center text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={16} /> : "Cancel appointment"}</button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-[#8FA89B] mt-4">Questions? Call <a href="tel:+18599795505" className="text-[#144F75]">859-979-5505</a></p>
      </div>
    </div>
  );
}
